import type { QueryClient } from "@tanstack/react-query";
import { Link, Outlet, createRootRouteWithContext, useRouterState } from "@tanstack/react-router";

import { LanguageSwitch } from "../components/shell/LanguageSwitch";
import { Toaster } from "../components/ui";
import { I18nProvider } from "../i18n/I18nProvider";

interface RouterContext {
  queryClient: QueryClient;
}

declare module "@tanstack/react-router" {
  interface StaticDataRouteOption {
    /** Set by every route rendered inside `PublicLayout`; see RootLayout below. */
    publicShell?: boolean;
    /** Set by the signed-in layout (`/app`), whose band carries the language switch (SP-032). */
    appShell?: boolean;
    /** A signed-in page that uses the full content width instead of the reading column. */
    wideLayout?: boolean;
  }
}

export const Route = createRootRouteWithContext<RouterContext>()({
  component: RootLayout,
  notFoundComponent: () => (
    <main className="mx-auto max-w-3xl px-4 py-8">
      <h1>Page not found</h1>
      <Link to="/">Scalephant</Link>
    </main>
  ),
});

function RootLayout() {
  // The public shell (features/marketing) and the signed-in band carry their own language
  // switch, so the fallback one below would be a second control with the same label. Routes
  // inside them say so through `staticData`.
  const hasOwnLanguageSwitch = useRouterState({
    select: (state) =>
      state.matches.some(
        (match) => match.staticData.publicShell === true || match.staticData.appShell === true,
      ),
  });
  return (
    <I18nProvider>
      <div className="flex min-h-screen flex-col [&>main]:flex-1">
        <Outlet />
        {hasOwnLanguageSwitch ? null : (
          <footer className="text-muted-foreground p-4 text-right">
            <LanguageSwitch />
          </footer>
        )}
        <Toaster />
      </div>
    </I18nProvider>
  );
}
